import React from "react";
import DrawerModal from "~/common/DrawerModal";
import Icon from "~/common/Icons/Standard";
import { ConfigurationOption } from "../ProductDetailPageResult";
import { VirtualGroupingOptionsToMap } from "./VirtualGroupingModel";
import Button from "~/common/Buttons";
import { findSelectedOption } from "./VirtualGroupingHelper";

interface Props {
  selectedOptions: readonly ConfigurationOption[];
  virtualGroupingTileProduct: VirtualGroupingOptionsToMap;
  handleClickVirtualGroupingVariation: (
    name: string,
    value: string,
    validOption: boolean
  ) => void;
  virtualGroupingMobileDialogRef: React.RefObject<HTMLDialogElement>;
  toggleShowMobileVirtualGroupingDrawer: () => void;
}

const ProductDetailVirtualGroupingMobileDrawer = ({
  virtualGroupingTileProduct,
  handleClickVirtualGroupingVariation,
  selectedOptions,
  virtualGroupingMobileDialogRef,
  toggleShowMobileVirtualGroupingDrawer,
}: Props) => {
  const setFocusAfterClose = () => {
    if (virtualGroupingMobileDialogRef.current) {
      const viewAllButton = document.getElementById(
        "viewAllVirtualGroupingItems"
      );
      viewAllButton?.focus();
    }
  };

  return (
    <DrawerModal
      dialogRef={virtualGroupingMobileDialogRef}
      title={virtualGroupingTileProduct.name}
      subtitle={`${virtualGroupingTileProduct.variations.length} options`}
      id="virtualGroupingMobileModal"
      leftControlContent={
        <Icon
          name="remix-close"
          width="28px"
          height="28px"
          className="fill-green-700"
        />
      }
      leftControlFunction={toggleShowMobileVirtualGroupingDrawer}
      spacerClasses="p-0"
      dialogClasses="bg-white rounded-t-lg"
      headingClasses="font-bold text-xl leading-tight text-gray-900 mb-0 p-0 text-center flex-[1_1_33.33%]"
      headerClasses="relative items-center bg-transparent h-12 py-2.5 border-0 border-b border-solid"
      headerStyles={{ borderColor: "rgba(0,0,0,0.1)" }}
      subtitleClasses="mb-0 text-sm leading-tight text-gray-700"
      controlClasses="bg-transparent py-0"
      onClose={setFocusAfterClose}
      leftControlArialLabelText="Close"
    >
      <div data-testid="variationTileMobileDrawer">
        {virtualGroupingTileProduct.variations.map((variation) => (
          <Button
            type="button"
            aria-pressed={
              findSelectedOption(variation, selectedOptions) ? "true" : "false"
            }
            className="bg-transparent border-0 border-none w-full px-3 rounded-lg"
            color="custom"
            size="custom"
            focusClasses="focus-visible:outline focus-visible:outline-offset-[-6px] focus-visible:outline-2"
            value={variation.value}
            onClick={(e) => {
              handleClickVirtualGroupingVariation(
                variation.name,
                e.currentTarget.value,
                virtualGroupingTileProduct.validOptions.includes(
                  e.currentTarget.value
                )
              );
              toggleShowMobileVirtualGroupingDrawer();
            }}
            key={variation.value}
          >
            <div
              className="flex flex-col border-b border-t-0 border-l-0 border-r-0 border-solid"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-x-4 py-2 px-2.5">
                  {/* Currently in phase 1 images will only show if the variation name is Color */}
                  {variation.name === "Color" ? (
                    <div className="w-16 h-16 relative">
                      {!variation.isInStock && (
                        <span
                          className="absolute border border-solid border-gray-500 top-1/2 shadow inset-x-0"
                          style={{
                            transform: "rotate(-55deg)",
                            zIndex: 1,
                            boxShadow:
                              "0px -1px 0px #FFFFFF, 0px 1px 0px #FFFFFF",
                          }}
                        />
                      )}
                      <img
                        className={`w-full mix-blend-multiply ${
                          !variation.isInStock ? "opacity-50" : ""
                        }`}
                        src={variation.previewImage}
                        alt=""
                      />
                    </div>
                  ) : null}
                  <div className="py-3">
                    <p
                      className={`mb-0 text-lg font-semibold leading-5 ${
                        !variation.isInStock ? "text-gray-700" : "text-gray-800"
                      }`}
                    >
                      {variation.value}
                    </p>
                  </div>
                </div>
                {/* This will add a checkmark to the product that is already selected before the mobile drawer opens and removes it when a new item is selected */}
                {findSelectedOption(variation, selectedOptions) ? (
                  <div>
                    <Icon
                      name="check"
                      className="fill-green-700"
                      width="24px"
                      height="24px"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </Button>
        ))}
      </div>
    </DrawerModal>
  );
};

export default ProductDetailVirtualGroupingMobileDrawer;
